// Immutable marker revision history for the marker detail drawer. The caller
// owns revision pagination and the shared directory join, keeping this View
// independent of query providers.

import type { components } from "../api/schema";
import { formatDateTime } from "../lib/format";
import type { ListQueryResult } from "../lib/useListQuery";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  Section,
  Skeleton,
  Stack,
  Text,
} from "../ui";
import {
  uniqueIds,
  useUserDirectoryQuery,
  UserLabel,
  userMap,
} from "./shared/userDirectory";

type MarkerRevisionResponse = components["schemas"]["MarkerRevisionResponse"];

export type MarkerRevisionHistoryResult =
  ListQueryResult<MarkerRevisionResponse> & {
    usersById?: ReadonlyMap<string, components["schemas"]["UserResponse"]>;
  };

export function useMarkerRevisionAuthors(
  history: MarkerRevisionHistoryResult,
): MarkerRevisionHistoryResult {
  const users = useUserDirectoryQuery(
    uniqueIds(history.items.map((revision) => revision.created_by)).join(","),
    "marker-revision-authors",
  );
  return { ...history, usersById: userMap(users.data?.items ?? []) };
}

export interface MarkerRevisionHistoryProps {
  currentRevisionId: string;
  history: MarkerRevisionHistoryResult;
}

export function MarkerRevisionHistory({
  currentRevisionId,
  history,
}: MarkerRevisionHistoryProps) {
  return (
    <Section heading="Revision history">
      {history.isPending ? (
        <Skeleton
          label="Loading revision history"
          rows={3}
          shape="detail-field"
        />
      ) : history.errorMessage !== undefined ? (
        <InlineError>{history.errorMessage}</InlineError>
      ) : history.items.length === 0 ? (
        <Text variant="muted">No revisions recorded.</Text>
      ) : (
        <Stack gap={3}>
          {/* Law: docs/09 (§6). Rows show display name, description
              (falling back to display name), resolved author, and time. */}
          {history.items.map((revision) => (
            <Stack gap={1} key={revision.id}>
              <Inline align="center" gap={2}>
                <Text as="span">
                  <strong>Revision {revision.revision}</strong>
                </Text>
                {revision.id === currentRevisionId ? (
                  <Badge tone="success">Current</Badge>
                ) : null}
              </Inline>
              <Text>{revision.display_name}</Text>
              <Text variant="muted">
                {revision.description ?? revision.display_name}
              </Text>
              <Text variant="muted">
                {revision.created_by == null ? (
                  "System"
                ) : (
                  <>
                    Created by{" "}
                    <UserLabel
                      compact
                      fallbackId={revision.created_by}
                      user={history.usersById?.get(revision.created_by)}
                    />
                  </>
                )}
                {` · ${formatDateTime(revision.created_at)}`}
              </Text>
            </Stack>
          ))}
          {history.hasNextPage ? (
            <Inline>
              <Button
                disabled={history.isFetchingNextPage}
                size="sm"
                variant="secondary"
                onClick={history.fetchNextPage}
              >
                {history.isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </Inline>
          ) : null}
        </Stack>
      )}
    </Section>
  );
}
